import React, {useEffect, useState} from 'react';

import {useFormatNumber, useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Link} from '@udemy/react-core-components';

import {ApiInstructor} from 'src/features/google-ai-path-page/types/course';

import styles from './instructor-overview.module.less';

export interface InstructorStatsProps {
    instructor: ApiInstructor;
}

/**
 * InstructorStats component displays statistical information about a course instructor.
 *
 * @example
 * ```jsx
 * <InstructorStats instructor={instructor} />
 * ```
 */

export const InstructorStats: React.FC<InstructorStatsProps> = ({instructor}) => {
    const {interpolate, gettext} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {
        avg_rating,
        num_published_courses,
        num_visible_taught_courses,
        total_num_reviews,
        description,
    } = instructor;

    const [isInstructorStatsLoaded, setIsInstructorStatsLoaded] = useState(false);

    useEffect(() => {
        setIsInstructorStatsLoaded(true);
    }, []);

    const starRatingText = (avg_rating ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: 1,
    });
    const courseCount = num_published_courses ?? num_visible_taught_courses ?? 0;
    const numCourseText = courseCount ? formatNumber(courseCount) : 0;
    const numReviewText = total_num_reviews ? formatNumber(total_num_reviews) : 0;

    return (
        <div className={styles['instructor-stats']}>
            <div className={styles['instructor-stats-header']}>
                <h2 className={styles['instructor-stats-title']}>
                    <Link
                        to={instructor.url || '#'}
                        className={styles['instructor-stats-title-link']}
                        disableRouter={true}
                    >
                        {instructor?.display_name || gettext('Google')}
                    </Link>
                </h2>
            </div>
            <div className={styles['instructor-stats-row']}>
                <div className={styles['instructor-stats-logo']}>
                    <Icon glyph="google" size="xxlarge" label="Google" />
                </div>
                <div className={styles['instructor-stats-list']}>
                    <div className={styles['instructor-stats-item']}>
                        <Icon label={false} glyph="rating-star" className={styles['stat-icon']} />
                        <span>
                            {interpolate(
                                gettext('%(rating)s instructor rating'),
                                {
                                    rating: starRatingText,
                                },
                                true,
                            )}
                        </span>
                    </div>
                    <div className={styles['instructor-stats-item']}>
                        <Icon label={false} glyph="notification" className={styles['stat-icon']} />
                        <span>
                            {interpolate(
                                gettext('%(count)s reviews'),
                                {count: numReviewText},
                                true,
                            )}
                        </span>
                    </div>
                    <div className={styles['instructor-stats-item']}>
                        <Icon label={false} glyph="play" className={styles['stat-icon']} />
                        <span>
                            {interpolate(
                                gettext('%(count)s courses'),
                                {count: numCourseText},
                                true,
                            )}
                        </span>
                    </div>
                </div>
            </div>
            {isInstructorStatsLoaded && description && (
                <p
                    className={styles['instructor-stats-description']}
                    dangerouslySetInnerHTML={{__html: description}}
                />
            )}
        </div>
    );
};
